
import { useState, useEffect } from 'react';

interface RadioSpin {
  track: string;
  station: string;
  location: string;
  datetime: string;
}

interface TrackSpins {
  title: string;
  spins: number;
}

interface TotalSpinStats {
  total: number;
  increase: number;
  stations: number;
  countries: number;
  topMarket: string;
  topMarketSpins: number;
  topTrack: string;
  topTrackSpins: number;
}

interface WeeklySpinData {
  week: string;
  spins: number;
}

interface GenreData {
  name: string;
  value: number;
}

interface TimeOfDayData {
  name: string;
  spins: number;
}

interface DayOfWeekData {
  day: string;
  spins: number;
}

interface CountryData {
  country: string;
  spins: number;
}

interface DemographicData {
  name: string;
  value: number;
}

interface InsightsData {
  genreBreakdown: GenreData[];
  timeOfDayData: TimeOfDayData[];
  dayOfWeekData: DayOfWeekData[];
  countryData: CountryData[];
  listenerDemographics: DemographicData[];
}

interface TrackData {
  id: string;
  title: string;
}

interface RegionHighlight {
  region: string;
  spins: number;
  stations: number;
  trend: number;
}

interface WorldMapData {
  tracks: TrackData[];
  highlights: RegionHighlight[];
}

export const useMockRadioData = () => {
  const [recentSpins, setRecentSpins] = useState<RadioSpin[]>([]);
  const [totalSpins, setTotalSpins] = useState<TotalSpinStats>({
    total: 0,
    increase: 0,
    stations: 0,
    countries: 0,
    topMarket: '',
    topMarketSpins: 0,
    topTrack: '',
    topTrackSpins: 0
  });
  const [spinsByTrack, setSpinsByTrack] = useState<TrackSpins[]>([]);
  const [weeklySpinsData, setWeeklySpinsData] = useState<WeeklySpinData[]>([]);
  const [insightsData, setInsightsData] = useState<InsightsData>({
    genreBreakdown: [],
    timeOfDayData: [],
    dayOfWeekData: [],
    countryData: [],
    listenerDemographics: []
  });
  const [worldMapData, setWorldMapData] = useState<WorldMapData>({
    tracks: [],
    highlights: []
  });

  useEffect(() => {
    // Mock data for recent spins
    const mockRecentSpins: RadioSpin[] = [
      { track: 'Midnight Dream', station: 'KEXP 90.3 FM', location: 'Seattle, USA', datetime: 'Today, 14:32' },
      { track: 'Summer Haze', station: 'BBC Radio 1', location: 'London, UK', datetime: 'Today, 12:15' },
      { track: 'Lost in Translation', station: 'Triple J', location: 'Sydney, Australia', datetime: 'Today, 09:45' },
      { track: 'Midnight Dream', station: 'Radio Nova', location: 'Paris, France', datetime: 'Yesterday, 21:30' },
      { track: 'Echoes', station: 'KCRW', location: 'Los Angeles, USA', datetime: 'Yesterday, 18:15' },
      { track: 'Summer Haze', station: 'SiriusXM Alt Nation', location: 'USA', datetime: '2 days ago' },
      { track: 'Lost in Translation', station: 'Radio X', location: 'Manchester, UK', datetime: '3 days ago' },
    ];
    
    // Mock data for total spins
    const mockTotalSpins: TotalSpinStats = {
      total: 14328,
      increase: 1243,
      stations: 327,
      countries: 42,
      topMarket: 'Los Angeles',
      topMarketSpins: 1890,
      topTrack: 'Midnight Dream',
      topTrackSpins: 5621,
    };
    
    // Mock data for spins by track
    const mockSpinsByTrack: TrackSpins[] = [
      { title: 'Midnight Dream', spins: 5621 },
      { title: 'Summer Haze', spins: 3842 },
      { title: 'Lost in Translation', spins: 2510 },
      { title: 'Echoes', spins: 1455 },
      { title: 'Neon Lights', spins: 900 },
    ];
    
    // Mock data for weekly spins
    const mockWeeklySpinsData: WeeklySpinData[] = [
      { week: 'Week 1', spins: 2840 },
      { week: 'Week 2', spins: 3250 },
      { week: 'Week 3', spins: 3780 },
      { week: 'Week 4', spins: 4458 },
    ];
    
    // Mock insights data
    const mockInsightsData: InsightsData = {
      genreBreakdown: [
        { name: 'Alternative', value: 85 },
        { name: 'Pop', value: 65 },
        { name: 'Rock', value: 45 },
        { name: 'Electronic', value: 30 },
        { name: 'Urban', value: 15 },
      ],
      timeOfDayData: [
        { name: 'Morning (6-10)', spins: 2300 },
        { name: 'Midday (10-2)', spins: 3100 },
        { name: 'Afternoon (2-6)', spins: 4200 },
        { name: 'Evening (6-10)', spins: 3500 },
        { name: 'Night (10-2)', spins: 1000 },
        { name: 'Late Night (2-6)', spins: 228 },
      ],
      dayOfWeekData: [
        { day: 'Monday', spins: 1800 },
        { day: 'Tuesday', spins: 1900 },
        { day: 'Wednesday', spins: 2050 },
        { day: 'Thursday', spins: 2200 },
        { day: 'Friday', spins: 2850 },
        { day: 'Saturday', spins: 1850 },
        { day: 'Sunday', spins: 1678 },
      ],
      countryData: [
        { country: 'United States', spins: 6500 },
        { country: 'United Kingdom', spins: 2300 },
        { country: 'Australia', spins: 1800 },
        { country: 'Canada', spins: 1500 },
        { country: 'Germany', spins: 950 },
        { country: 'France', spins: 780 },
        { country: 'Japan', spins: 450 },
        { country: 'Brazil', spins: 380 },
        { country: 'Mexico', spins: 350 },
        { country: 'Sweden', spins: 320 },
      ],
      listenerDemographics: [
        { name: '18-24', value: 25 },
        { name: '25-34', value: 35 },
        { name: '35-44', value: 20 },
        { name: '45-54', value: 12 },
        { name: '55+', value: 8 },
      ],
    };
    
    // Mock world map data
    const mockWorldMapData: WorldMapData = {
      tracks: [
        { id: 'midnight-dream', title: 'Midnight Dream' },
        { id: 'summer-haze', title: 'Summer Haze' },
        { id: 'lost-in-translation', title: 'Lost in Translation' },
        { id: 'echoes', title: 'Echoes' },
        { id: 'neon-lights', title: 'Neon Lights' },
      ],
      highlights: [
        { region: 'North America', spins: 8350, stations: 145, trend: 12 },
        { region: 'Europe', spins: 4230, stations: 98, trend: 8 },
        { region: 'Asia-Pacific', spins: 1748, stations: 84, trend: 15 },
      ],
    };
    
    setRecentSpins(mockRecentSpins);
    setTotalSpins(mockTotalSpins);
    setSpinsByTrack(mockSpinsByTrack);
    setWeeklySpinsData(mockWeeklySpinsData);
    setInsightsData(mockInsightsData);
    setWorldMapData(mockWorldMapData);
  }, []);

  return {
    recentSpins,
    totalSpins,
    spinsByTrack,
    weeklySpinsData,
    insightsData,
    worldMapData
  };
};
